import React, { useMemo } from 'react';
import { Box, TableCell, Tooltip } from '@mui/material';
import { MarginHighlightCellProps } from '../types';
import {
  GrossMarginHighlightColor,
  NetMarginHighlightColor,
} from '../../../constants/marginTracker';

import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

export const MarginHighlightCell = ({
  children,
  marginHighlights,
  value,
  marginCategory,
  ...props
}: MarginHighlightCellProps) => {
  const marginType = useMemo(() => {
    return marginHighlights.find(
      (marginHighlight) =>
        value != null &&
        (marginHighlight.min ?? -Infinity) <= value &&
        (marginHighlight.max ?? Infinity) >= value,
    )?.type;
  }, [value, marginHighlights]);

  const netMarginHelperText = useMemo(() => {
    if (!value) return '';
    if (value >= 20) return '20% and above - Approved margin, best case.';
    if (value >= 15) return '15 .. 19.99% - Approved margin, decent case.';
    if (value >= 10) return '10 .. 14.99% - Approved margin, minimum margin.';
    if (value >= 5) return '5 .. 9.99% - Approval required, low margin.';
    return '4.99% and below - No go, negative margin.';
  }, [value]);

  const grossMarginHelperText = useMemo(() => {
    if (!value) return '';
    if (value >= 40) return '40% and above - on target.';
    if (value >= 37) return '37 .. 39.99% - yellow.';
    if (value >= 34) return '34 .. 36.99% - red.';
    if (value >= 32) return '32 .. 33.99% - panic.';
    return "31.99% and below - shouldn' happen.";
  }, [value]);

  const themeColor =
    marginCategory === 'Gross Margin'
      ? GrossMarginHighlightColor[marginType || ''] ?? 'text.primary'
      : NetMarginHighlightColor[marginType || ''] ?? 'text.primary';

  return (
    <TableCell
      {...props}
      width={60}
      sx={{
        fontSize: '0.8rem',
        fontWeight: 500,
        maxWidth: 90,
        ...(props.sx ?? {}),
      }}
    >
      <Tooltip
        title={
          marginCategory === 'Gross Margin'
            ? grossMarginHelperText
            : netMarginHelperText
        }
        placement="top"
      >
        <Box
          sx={{
            display: 'flex',
            gap: 0.5,
            justifyContent: 'end',
            alignItems: 'center',
          }}
        >
          {children}
          {value != null ? (
            <FiberManualRecordIcon
              sx={{
                fontSize: '20px',
                color: themeColor,
                flexShrink: 0,
              }}
            />
          ) : null}
        </Box>
      </Tooltip>
    </TableCell>
  );
};
